/**
 * The comment thread on an item or mission: Markdown comments with screenshots and files, a
 * composer (paste an image straight into the text), edit for the author, delete for the author
 * or an admin.
 */
import { Pencil, Trash2 } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ClipboardEvent } from 'react';
import { COMMENT_MAX, attachmentRef, attachmentRefsIn } from '@orgus/protocol';
import type { Comment, CommentTarget, Id } from '@orgus/protocol';
import { AttachmentInput, AttachmentList, Button, Composer, ConfirmDialog, ErrorState, IconButton, Kbd, Markdown, MarkdownEditor, RelativeTime, Skeleton, filesFrom, hasMod, toUiError, toast } from '@orgus/ui';
import type { AttachmentView, UiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { addComment, canChange, deleteComment, editComment, removeAttachment, stripAttachmentRef, toView, uploaderFor, useAttachmentIndex, useCaller, useCommentsAvailable, useCommentsOn, useResolveAttachment } from './data.ts';

export function CommentsThread({ target }: { target: CommentTarget }) {
  const available = useCommentsAvailable();
  const comments = useCommentsOn(target);
  const caller = useCaller();
  const phase = useOrgus((s) => s.phase);

  if (phase === 'loading' || available === null) return <Skeleton className="block h-20 w-full" />;
  if (!available) return <p className="text-ui text-fg-subtle">Update Orgus on the hub machine to use comments.</p>;
  return (
    // No "No comments yet" line: the item's history sits right below the thread, and the box says
    // what to do.
    <div className="flex flex-col gap-4">
      {comments.length > 0 && (
        <ol className="flex flex-col gap-4">
          {comments.map((c) => (
            <CommentRow key={c.id} c={c} canEdit={caller?.id === c.by} canDelete={canChange(caller, c.by)} />
          ))}
        </ol>
      )}
      {caller?.role !== 'viewer' && <CommentBox target={target} />}
    </div>
  );
}

function CommentRow({ c, canEdit, canDelete }: { c: Comment; canEdit: boolean; canDelete: boolean }) {
  const index = useAttachmentIndex();
  const files = useMemo(() => c.attachmentIds.map((id) => index.get(id)).filter((a) => !!a && a.target?.id === c.id).map((a) => toView(a!)), [c, index]);
  const resolve = useResolveAttachment(files);
  // Files the text already shows are not listed again below it.
  const listed = useMemo(() => {
    const inline = new Set(attachmentRefsIn(c.body));
    return files.filter((f) => !(f.isImage && inline.has(f.id)));
  }, [files, c.body]);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(c.body);
  const [added, setAdded] = useState<AttachmentView[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const upload = useMemo(() => uploaderFor(null), []);
  const caller = useCaller();

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await editComment(c.id, { body, attachmentIds: [...c.attachmentIds, ...added.map((a) => a.id)] });
      setEditing(false);
      setAdded([]);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2 text-caption">
          <span className="text-ui font-medium text-fg">{c.by}</span>
          <RelativeTime ts={c.createdAt} className="text-fg-subtle" />
          {c.editedAt && <span className="text-fg-subtle">edited</span>}
          <span className="ml-auto flex items-center gap-1">
            {canEdit && !editing && <IconButton label="Edit comment" icon={<Pencil />} size="xs" onClick={() => (setBody(c.body), setEditing(true))} />}
            {canDelete && <IconButton label="Delete comment" icon={<Trash2 />} size="xs" variant="danger" onClick={() => setConfirm(true)} />}
          </span>
        </div>
        {editing ? (
          <div className="flex flex-col gap-2">
            <MarkdownEditor
              value={body}
              onChange={setBody}
              rows={4}
              maxLength={COMMENT_MAX}
              upload={upload}
              onUploaded={(a) => setAdded((x) => [...x, a])}
              resolveAttachment={resolve}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && hasMod(e)) {
                  e.preventDefault();
                  void save();
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => (setEditing(false), setBody(c.body))}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" loading={busy} onClick={save} disabled={!body.trim() && !c.attachmentIds.length && !added.length}>
                Save comment
              </Button>
            </div>
          </div>
        ) : (
          c.body.trim() && <Markdown source={c.body} resolveAttachment={resolve} onOpenAttachment={setOpenId} size="sm" />
        )}
        <AttachmentList
          items={listed}
          lightboxItems={files}
          openId={openId}
          onOpenChange={setOpenId}
          canRemove={(a) => canChange(caller, a.by)}
          onRemove={async (a) => {
            setError(null);
            try {
              await removeAttachment(a.id);
            } catch (e) {
              setError(toUiError(e));
            }
          }}
        />
        {error && <ErrorState variant="inline" error={error} />}
      </div>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Delete this comment?"
        description={c.attachmentIds.length ? `Its ${c.attachmentIds.length === 1 ? 'attachment goes' : `${c.attachmentIds.length} attachments go`} with it.` : 'It is removed for everyone.'}
        confirmLabel="Delete comment"
        onConfirm={async () => {
          await deleteComment(c.id);
          toast.success('Comment deleted');
        }}
      />
    </li>
  );
}

/**
 * The new-comment box: one quiet message box like the session drawer's, with the attach button and
 * Comment inside its frame. A pasted screenshot uploads and lands in the text as Markdown.
 */
function CommentBox({ target }: { target: CommentTarget }) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<AttachmentView[]>([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<UiError | null>(null);
  const upload = useMemo(() => uploaderFor(null), []);
  const empty = !body.trim() && !files.length;

  const submit = async () => {
    if (empty || busy || uploading) return;
    setBusy(true);
    setError(null);
    try {
      await addComment({ target, body, attachmentIds: files.map((f) => f.id) });
      setBody('');
      setFiles([]);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = filesFrom(e.clipboardData?.items);
    if (!pasted.length) return;
    e.preventDefault();
    setError(null);
    for (const file of pasted) {
      setUploading((n) => n + 1);
      upload(file, { onProgress: () => undefined, signal: new AbortController().signal })
        .then((a) => {
          setFiles((x) => [...x, a]);
          const ref = `${a.isImage ? '!' : ''}[${a.name.replace(/[[\]]/g, '')}](${attachmentRef(a.id)})`;
          setBody((b) => `${b}${b && !b.endsWith('\n') ? '\n' : ''}${ref}\n`);
        })
        .catch((err: unknown) => setError(toUiError(err)))
        .finally(() => setUploading((n) => n - 1));
    }
  };
  const drop = async (id: Id) => {
    setFiles((x) => x.filter((f) => f.id !== id));
    setBody((b) => stripAttachmentRef(b, id));
    await removeAttachment(id).catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-2">
      <Composer
        value={body}
        rows={1}
        maxLength={COMMENT_MAX}
        placeholder="Add a comment"
        aria-label="Add a comment"
        onChange={(e) => setBody(e.target.value)}
        onPaste={onPaste}
        onSubmit={() => void submit()}
        tools={<AttachmentInput variant="button" upload={upload} onUploaded={(a) => setFiles((x) => [...x, a])} />}
        hint={<Kbd keys={['mod', 'enter']} />}
        send={
          <Button size="sm" variant="primary" loading={busy} disabled={empty || uploading > 0} onClick={() => void submit()}>
            Comment
          </Button>
        }
        note={uploading > 0 ? `Uploading ${uploading} ${uploading === 1 ? 'file' : 'files'}` : undefined}
      />
      <AttachmentList items={files} canRemove={() => true} onRemove={(a) => void drop(a.id)} />
      {error && <ErrorState variant="inline" error={error} />}
    </div>
  );
}
